/**
 * 妖怪生成処理のカスタムフック
 * 進捗管理とデータ・画像の一括表示機能を含む
 */

import { useState, useCallback } from 'react';
import { YokaiInput, YokaiOutput } from '../types/yokai';
import { generateYokaiDataOnly, generateYokaiImagesOnly } from '../services/yokaiGenerator';
import { logger } from '../utils/logger';
import { ErrorHandler, YokaiAppError } from '../utils/errorHandler';

// 進捗管理定数
const PROGRESS_STEPS = {
  INITIAL: 0,
  DATA_START: 10,
  DATA_COMPLETE: 50,
  DATA_PROCESSED: 60,
  IMAGE_START: 70,
  IMAGE_COMPLETE: 90,
  FINAL: 100
} as const;

interface UseYokaiGeneratorResult {
  yokaiData: YokaiOutput | null;
  isLoading: boolean;
  error: string | null;
  progress: number;
  generateYokai: (input: YokaiInput) => Promise<void>;
  clearError: () => void;
  retry: () => Promise<void>;
}

interface UseYokaiGeneratorOptions {
  onSuccess?: (yokaiData: YokaiOutput) => void;
  onError?: (error: YokaiAppError) => void;
}

export const useYokaiGenerator = (options: UseYokaiGeneratorOptions = {}): UseYokaiGeneratorResult => {
  const [yokaiData, setYokaiData] = useState<YokaiOutput | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [lastInput, setLastInput] = useState<YokaiInput | null>(null);
  const [progressInterval, setProgressInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // 進捗を1%刻みで滑らかに更新するアニメーション関数
  const animateProgress = useCallback((fromPercent: number, toPercent: number, duration: number = 2000) => {
    return new Promise<void>((resolve) => {
      if (progressInterval) {
        clearInterval(progressInterval);
        setProgressInterval(null);
      }

      const steps = toPercent - fromPercent;
      const stepDuration = duration / steps;
      let currentPercent = fromPercent;

      const interval = setInterval(() => {
        currentPercent += 1;
        setProgress(currentPercent);

        if (currentPercent >= toPercent) {
          clearInterval(interval);
          setProgressInterval(null);
          resolve();
        }
      }, stepDuration);

      setProgressInterval(interval);
    });
  }, [progressInterval]);

  // API完了時に進捗アニメーションを停止してターゲット値に設定
  const completeProgress = useCallback((targetPercent: number) => {
    if (progressInterval) {
      clearInterval(progressInterval);
      setProgressInterval(null);
    }
    setProgress(targetPercent);
  }, [progressInterval]);

  const generateYokai = useCallback(async (input: YokaiInput) => {
    logger.userAction('妖怪生成開始', input);
    setIsLoading(true);
    setError(null);
    setYokaiData(null);
    setProgress(PROGRESS_STEPS.INITIAL);
    setLastInput(input);
    
    try {
      // 1. 妖怪データ生成（画像と一緒に最終表示するまで非表示）
      setProgress(PROGRESS_STEPS.DATA_START);
      logger.yokaiGeneration('データ生成', '妖怪データ生成中...');
      
      // データ生成中の進捗アニメーションを開始（バックグラウンドで実行）
      animateProgress(PROGRESS_STEPS.DATA_START, PROGRESS_STEPS.DATA_COMPLETE, 5000);
      
      const basicData = await generateYokaiDataOnly(input);
      
      // API完了時に進捗を50%に設定
      completeProgress(PROGRESS_STEPS.DATA_COMPLETE);
      setProgress(PROGRESS_STEPS.DATA_PROCESSED);
      logger.yokaiGeneration('データ生成', '妖怪データ生成完了');
      
      // 2. 画像生成
      try {
        setProgress(PROGRESS_STEPS.IMAGE_START);
        logger.yokaiGeneration('画像生成', '画像生成中...');
        
        // 画像生成中の進捗アニメーションを開始（バックグラウンドで実行）
        animateProgress(PROGRESS_STEPS.IMAGE_START, PROGRESS_STEPS.IMAGE_COMPLETE, 6000);
        
        const images = await generateYokaiImagesOnly(basicData.imagePrompt);
        
        // API完了時に進捗を90%に設定
        completeProgress(PROGRESS_STEPS.IMAGE_COMPLETE);
        
        // 全ての生成完了後に妖怪データを画像と一緒に表示
        const finalYokaiData: YokaiOutput = {
          name: basicData.name,
          description: basicData.description,
          likes: basicData.likes,
          dislikes: basicData.dislikes,
          catchphrase: basicData.catchphrase,
          images,
          imagesLoading: false
        };
        
        setYokaiData(finalYokaiData);
        setIsLoading(false); // 全ての生成プロセス完了時にローディング解除
        setProgress(PROGRESS_STEPS.FINAL);
        logger.yokaiGeneration('画像生成', '画像生成完了');
        
        if (options.onSuccess) {
          options.onSuccess(finalYokaiData);
        }
        
      } catch (imageError) {
        logger.error('YOKAI_GEN', '画像生成エラー', imageError);
        
        // エラーハンドリング
        let appError: YokaiAppError;
        if (imageError instanceof YokaiAppError) {
          appError = imageError;
        } else {
          appError = ErrorHandler.handleGenericError(imageError, '画像生成');
        }
        
        // 画像エラー時も妖怪データを表示（画像なしで）
        const errorYokaiData: YokaiOutput = {
          name: basicData.name,
          description: basicData.description,
          likes: basicData.likes,
          dislikes: basicData.dislikes,
          catchphrase: basicData.catchphrase,
          images: [],
          imagesLoading: false,
          imageError: appError.userMessage
        };
        
        setYokaiData(errorYokaiData);
        setIsLoading(false); // 画像生成エラー時もローディング解除
        setProgress(PROGRESS_STEPS.FINAL);
        
        if (options.onError) {
          options.onError(appError);
        }
      }
      
    } catch (err) {
      logger.error('YOKAI_GEN', '妖怪データ生成エラー', err);
      
      // エラーハンドリング
      let appError: YokaiAppError;
      if (err instanceof YokaiAppError) {
        appError = err;
      } else {
        appError = ErrorHandler.handleGenericError(err, '妖怪データ生成');
      }
      
      setError(appError.userMessage);
      setIsLoading(false);
      setProgress(PROGRESS_STEPS.INITIAL);
      
      if (options.onError) {
        options.onError(appError);
      }
    }
  }, [options]);

  const retry = useCallback(async () => {
    if (lastInput) {
      logger.userAction('妖怪生成再試行');
      await generateYokai(lastInput);
    }
  }, [lastInput, generateYokai]);

  return {
    yokaiData,
    isLoading,
    error,
    progress,
    generateYokai,
    clearError,
    retry
  };
};